import { Injectable } from '@angular/core';
import {
	checkoutCouponApplicabilityRule,
	checkoutCouponDiscountRule,
	checkoutCouponValidatedResultRule,
	checkoutCouponValidatedValueRule,
	checkoutOrderDataRule,
	checkoutOrderDeliveryZoneRule,
	checkoutOrderProductsAvailabilityRule,
	checkoutOrderServiceAvailabilityRule,
	checkoutOrderTypeRule,
	checkoutOrderWorkDayRule,
} from './rules';
import { CheckoutOrderValidationRule } from './enum/checkout-order-validation.enum';
import { CheckoutCouponValidationRule } from './interfaces/checkout-coupon-validation.interface';
import {
	CheckoutAssociatedCardStrategy,
	CheckoutAssociatedCardResponsePipeline,
	CheckoutVaultedCardCreationPipeline,
} from './interfaces/checkout-credit-card.interface';
import {
	checkoutAssociatedCardResponseNormalizationRule,
	checkoutAssociatedCardResponseRule,
	checkoutVaultedCardCreationDataRule,
	checkoutVaultedCardCreationSuccessRule,
} from './rules/credit-card-vault.domain';
import { GuestAssociatedCardStrategy, LoggedInAssociatedCardStrategy } from './strategies';
import {
	checkoutAsyncErrorRule,
	checkoutAvailabilityResultRule,
	checkoutOrderDataFlowRule,
	checkoutOrderDeliveryZoneFlowRule,
	checkoutOrderPreparationRule,
	checkoutOrderProductsFlowRule,
	checkoutOrderServiceAvailabilityFlowRule,
	checkoutOrderTypeFlowRule,
	checkoutOrderValidationContextRule,
	checkoutOrderWorkDayFlowRule,
	checkoutPaymentDataRule,
	checkoutPriceChangesRule,
	checkoutUnavailableProductsRule,
} from './rules';
import { CheckoutValidationRule } from './interfaces/checkout-validation-flow.interface';
import { CheckoutPaymentStrategy } from './interfaces/checkout-payment.interface';
import { CardPaymentStrategy, CashPaymentStrategy } from './strategies';
import { PaymentMethodValid } from '@core/enums';
import {
	CheckoutOrderCreationRule,
	checkoutOrderCreationConfigurationRule,
	checkoutOrderPayloadRule,
} from './rules/checkout-order-creation.domain';
import { CheckoutOrderPayloadRule } from './interfaces/checkout-order-creation.interface';

@Injectable({ providedIn: 'root' })
export class CheckoutOrderFactory {
	createOrderCreationRules(): CheckoutOrderCreationRule[] {
		return [checkoutOrderCreationConfigurationRule];
	}

	createOrderPayloadRules(): [CheckoutOrderPayloadRule, ...CheckoutOrderPayloadRule[]] {
		return [checkoutOrderPayloadRule];
	}

	createOrderValidationRules(): CheckoutOrderValidationRule[] {
		return [
			checkoutOrderProductsAvailabilityRule,
			checkoutOrderDeliveryZoneRule,
			checkoutOrderTypeRule,
			checkoutOrderDataRule,
		];
	}

	createWorkDayValidationRules(): CheckoutOrderValidationRule[] {
		return [checkoutOrderWorkDayRule];
	}

	createServiceAvailabilityRules(): CheckoutOrderValidationRule[] {
		return [checkoutOrderServiceAvailabilityRule];
	}

	createCheckoutValidationRules(): CheckoutValidationRule[] {
		return [
			checkoutAsyncErrorRule,
			checkoutAvailabilityResultRule,
			checkoutUnavailableProductsRule,
			checkoutPriceChangesRule,
			checkoutOrderValidationContextRule,
			checkoutOrderProductsFlowRule,
			checkoutOrderDeliveryZoneFlowRule,
			checkoutOrderTypeFlowRule,
			checkoutOrderDataFlowRule,
			checkoutOrderWorkDayFlowRule,
			checkoutOrderServiceAvailabilityFlowRule,
			checkoutPaymentDataRule,
			checkoutOrderPreparationRule,
		];
	}

	createCheckoutPaymentStrategy(paymentMethod: string): CheckoutPaymentStrategy {
		return paymentMethod === PaymentMethodValid.CARD
			? new CardPaymentStrategy()
			: new CashPaymentStrategy();
	}

	createCouponValidationRules(): CheckoutCouponValidationRule[] {
		return [
			checkoutCouponDiscountRule,
			checkoutCouponApplicabilityRule,
			checkoutCouponValidatedValueRule,
			checkoutCouponValidatedResultRule,
		];
	}

	createAssociatedCardStrategy(isLoggedIn: boolean): CheckoutAssociatedCardStrategy {
		return isLoggedIn ? new LoggedInAssociatedCardStrategy() : new GuestAssociatedCardStrategy();
	}

	createAssociatedCardResponseRules(): CheckoutAssociatedCardResponsePipeline {
		return [checkoutAssociatedCardResponseRule, checkoutAssociatedCardResponseNormalizationRule];
	}

	createVaultedCardCreationRules(): CheckoutVaultedCardCreationPipeline {
		return [checkoutVaultedCardCreationSuccessRule, checkoutVaultedCardCreationDataRule];
	}
}
